import { Box, MenuItem, Select, Typography, Zoom } from '@mui/material';
import * as React from 'react';
import { Question } from '../../types/questionnaire.types';

interface RankingQuestionProps {
  question: Question;
  response: string;
  onRankingChange: (questionId: string, value: string) => void;
}

interface RankedItem {
  id: string;
  text: string;
  rank: number | null;
}

export const RankingQuestion: React.FC<RankingQuestionProps> = ({
  question,
  response,
  onRankingChange,
}) => {
  // Parse response to get current ranking
  const parseResponse = (): RankedItem[] => {
    if (!question.answers) return [];

    if (!response) {
      return question.answers.map((answer) => ({
        id: answer.id,
        text: answer.text,
        rank: null,
      }));
    }

    try {
      const parsed = JSON.parse(response);
      return question.answers.map((answer) => {
        const rankedItem = parsed.find((p: any) => p.answerId === answer.id);
        return {
          id: answer.id,
          text: answer.text,
          rank: rankedItem ? rankedItem.rank : null,
        };
      });
    } catch {
      return question.answers.map((answer) => ({
        id: answer.id,
        text: answer.text,
        rank: null,
      }));
    }
  };

  const [items, setItems] = React.useState<RankedItem[]>(parseResponse);

  // Update items when response changes externally
  React.useEffect(() => {
    setItems(parseResponse());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response, question.answers]);

  const updateResponse = (newItems: RankedItem[]) => {
    const rankedAnswers = newItems
      .filter((item) => item.rank !== null)
      .map((item) => ({
        answerId: item.id,
        answerText: item.text,
        rank: item.rank as number,
      }));

    onRankingChange(question.id, JSON.stringify(rankedAnswers));
  };

  const handleRankChange = (itemId: string, newRank: number | null) => {
    const newItems = [...items];
    const currentItem = newItems.find((item) => item.id === itemId);

    if (!currentItem) return;

    // Se escolheu "Sem posição", apenas remove o rank
    if (newRank === null) {
      currentItem.rank = null;
      setItems(newItems);
      updateResponse(newItems);
      return;
    }

    // Se a posição escolhida já está ocupada, faz o swap
    const itemAtNewRank = newItems.find(
      (item) => item.rank === newRank && item.id !== itemId
    );

    if (itemAtNewRank) {
      // Swap: o item que estava na posição escolhida vai para a posição antiga do item atual
      itemAtNewRank.rank = currentItem.rank;
    }

    currentItem.rank = newRank;
    setItems(newItems);
    updateResponse(newItems);
  };

  // Sort items by rank (items with rank first, then unranked)
  const sortedItems = React.useMemo(() => {
    const ranked = items
      .filter((item) => item.rank !== null)
      .sort((a, b) => (a.rank as number) - (b.rank as number));
    const unranked = items.filter((item) => item.rank === null);
    return [...ranked, ...unranked];
  }, [items]);

  if (!question.answers) return null;

  // Gera array de opções de ranking baseado no número de itens
  const totalItems = question.answers?.length || 0;
  const rankOptions = Array.from({ length: totalItems }, (_, i) => i + 1);

  return (
    <Box sx={{ width: '100%' }}>
      <Typography
        sx={{
          mb: 2,
          fontFamily: 'Roboto-Regular',
          fontSize: '10pt',
          color: '#6b7280',
          fontStyle: 'italic',
        }}
      >
        Selecione a posição de cada item de acordo com sua prioridade
      </Typography>

      {sortedItems.map((item, index) => {
        return (
          <Zoom
            in
            key={item.id}
            timeout={400 + index * 100}
            style={{ transitionDelay: `${index * 50}ms` }}
          >
            <Box
              sx={{
                mb: 1.5,
                borderRadius: '12px',
                border: '1px solid',
                borderColor: item.rank !== null ? '#667eea' : '#e5e7eb',
                background:
                  item.rank !== null
                    ? 'linear-gradient(135deg, #f0f4ff 0%, #e8eeff 100%)'
                    : '#ffffff',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                p: 2,
                gap: 2,
                '&:hover': {
                  background:
                    'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                  borderColor: '#667eea',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background:
                    item.rank !== null
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : '#e5e7eb',
                  color: item.rank !== null ? '#ffffff' : '#9ca3af',
                  fontFamily: 'Roboto-Bold',
                  fontSize: '14pt',
                  fontWeight: 'bold',
                  flexShrink: 0,
                  transition: 'all 0.3s ease',
                }}
              >
                {item.rank || '—'}
              </Box>

              <Typography
                sx={{
                  fontFamily: 'Roboto-Regular',
                  fontSize: '11pt',
                  color: '#374151',
                  flex: 1,
                  lineHeight: 1.5,
                }}
              >
                {item.text}
              </Typography>

              <Select
                value={item.rank || ''}
                onChange={(e) =>
                  handleRankChange(
                    item.id,
                    e.target.value === '' ? null : Number(e.target.value)
                  )
                }
                displayEmpty
                sx={{
                  minWidth: '120px',
                  borderRadius: '8px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e5e7eb',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#667eea',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#667eea',
                  },
                  '& .MuiSelect-select': {
                    fontFamily: 'Roboto-Regular',
                    fontSize: '10pt',
                    padding: '10px 14px',
                  },
                }}
              >
                <MenuItem value="" sx={{ fontFamily: 'Roboto-Regular' }}>
                  Sem posição
                </MenuItem>
                {rankOptions.map((rank) => (
                  <MenuItem
                    key={rank}
                    value={rank}
                    sx={{ fontFamily: 'Roboto-Regular' }}
                  >
                    {rank}ª posição
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Zoom>
        );
      })}
    </Box>
  );
};
